import { alpha, Avatar, Box, Button, ButtonBase, Chip, Divider, IconButton, InputBase, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import SearchIcon from '@mui/icons-material/Search'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useMediaQuery } from '@mui/material'
import { useBasePath } from 'components/Providers'
import { stringToColor } from 'components/Avatar'
import { search } from 'store/mentorship/mentorship'
import NoRecords from 'components/NoRecords'
import dayjs from 'dayjs'

const PersonCell = ({ person }) => {
  if (!person) return <Typography variant="body2" color="text.disabled">—</Typography>
  const name = `${person.lastName}, ${person.firstName}`
  const initials = `${person.firstName?.[0] || ''}${person.lastName?.[0] || ''}`.toUpperCase()
  const profilePicture = person?.profilePicture?.location
  const color = stringToColor(name)
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Avatar sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700, bgcolor: color, flexShrink: 0 }} src={profilePicture || undefined}>
        {initials}
      </Avatar>
      <Typography variant="body2" fontWeight={500}>{name}</Typography>
    </Stack>
  )
}

export default function EnrollmentList() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const basePath = useBasePath()
  const list = useSelector(state => state.mentorship.mentorship.list)
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState('active')
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'))

  useEffect(() => {
    if (!list) dispatch(search())
  }, [dispatch, Boolean(list)])

  const filtered = list?.filter(m => {
    const isActive = !m.endDate
    if (status === 'active' && !isActive) return false
    if (status === 'inactive' && isActive) return false
    if (!query) return true
    const menteeName = `${m.mentee?.firstName} ${m.mentee?.lastName}`.toLowerCase()
    const mentorName = `${m.mentor?.firstName} ${m.mentor?.lastName}`.toLowerCase()
    return menteeName.includes(query.toLowerCase()) || mentorName.includes(query.toLowerCase())
  })

  const columns = [
    {
      field: 'mentee',
      headerName: t('Mentee'),
      flex: 1.2,
      renderCell: params => <PersonCell person={params.row.mentee} />,
    },
    {
      field: 'mentor',
      headerName: t('Mentor'),
      flex: 1.2,
      renderCell: params => <PersonCell person={params.row.mentor} />,
    },
    {
      field: 'subMentor',
      headerName: t('Sub-Mentor'),
      flex: 1.2,
      renderCell: params => <PersonCell person={params.row.subMentor} />,
    },
    {
      field: 'startDate',
      headerName: t('Start Date'),
      width: 120,
      renderCell: params => (
        <Typography variant="body2" color="text.secondary">
          {dayjs(params.row.startDate).format('MM/DD/YYYY')}
        </Typography>
      ),
    },
    {
      field: 'endDate',
      headerName: t('Status'),
      width: 110,
      renderCell: params =>
        params.row.endDate ? (
          <Tooltip title={dayjs(params.row.endDate).format('MM/DD/YYYY')}>
            <Chip label={t('Ended')} size="small" variant="outlined" />
          </Tooltip>
        ) : (
          <Chip label={t('Active')} color="success" size="small" />
        ),
    },
    {
      field: 'notes',
      headerName: t('Notes'),
      width: 80,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: params =>
        params.row.notes ? (
          <Tooltip title={params.row.notes} placement="left" arrow>
            <IconButton size="small">
              <NotesOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null,
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 150,
      sortable: false,
      renderCell: params => (
        <Button
          size="small"
          variant="outlined"
          onClick={e => { e.stopPropagation(); navigate(`${basePath}/${params.row.id}`) }}
        >
          {t('More')}
        </Button>
      ),
    },
  ]

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '10px',
            overflow: 'hidden',
            height: 50,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 1.5, flex: 1 }}>
            <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            <InputBase
              placeholder={t('Search enrollments...')}
              value={query}
              onChange={e => setQuery(e.target.value)}
              sx={{ fontSize: 13, flex: 1 }}
            />
          </Stack>
          <Divider orientation="vertical" flexItem />
          {[
            { value: 'active', label: t('Active') },
            { value: 'inactive', label: t('Inactive') },
            { value: 'all', label: t('All') },
          ].map(({ value, label }) => (
            <ButtonBase
              key={value}
              onClick={() => setStatus(value)}
              sx={{
                px: 2,
                height: '100%',
                fontSize: 13,
                fontWeight: status === value ? 600 : 400,
                color: status === value ? 'primary.main' : 'text.secondary',
                bgcolor: status === value ? theme => alpha(theme.palette.primary.main, 0.08) : 'transparent',
                borderRight: '1px solid',
                borderColor: 'divider',
                transition: 'background 0.15s, color 0.15s',
                '&:last-child': { borderRight: 'none' },
              }}
            >
              {label}
            </ButtonBase>
          ))}
        </Paper>
        <Button variant="contained" startIcon={<GroupAddIcon />} onClick={() => navigate(`${basePath}/new`)}>
          {t('Add Enrollment')}
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
      >
      <DataGrid
        rows={filtered || []}
        columns={columns}
        getRowId={row => row.id}
        autoHeight
        disableColumnMenu
        hideFooter
        loading={!list}
        rowHeight={56}
        columnVisibilityModel={{ startDate: !isMobile }}
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: 'action.hover',
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'text.secondary',
          },
          '& .MuiDataGrid-row:hover': {
            bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
          },
          '& .MuiDataGrid-cell': {
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
        }}
        slots={{
          noRowsOverlay: () => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <NoRecords message={t('No enrollments found')} />
            </Box>
          ),
        }}
      />
      </Paper>
    </>
  )
}
